/** @jsxImportSource @emotion/react */

import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import { FC } from 'react'
import { Button, Divider, FormControlLabel, Typography } from '@mui/material'
import Checkbox from '@mui/material/Checkbox'
import { toast } from 'utils/toast'
import { findUserMfaInfo, findUserMfaInfoVariables } from '__generatedTypes__/findUserMfaInfo'
import { DISABLE_MFA_BY_ADMIN, ENABLE_MFA_BY_ADMIN, FIND_USER_MFA_INFO, UPDATE_USER_BY_ADMIN } from 'queries/users'
import { disableMfaByAdmin, disableMfaByAdminVariables } from '__generatedTypes__/disableMfaByAdmin'
import { enableMfaByAdmin, enableMfaByAdminVariables } from '__generatedTypes__/enableMfaByAdmin'
import { MfaTypeEnum } from '__generatedTypes__/globalTypes'
import { PageLoader } from 'components/PageLoader'
import { updateUserByAdmin, updateUserByAdminVariables } from '__generatedTypes__/updateUserByAdmin'
import * as commonClasses from '../styles'
import { RefetchUser } from '../hooks'
import * as classes from './styles'

type Props = {
  userId: string
  email: string
  isMandatoryModalEnabled: boolean
  refetchUser: RefetchUser
}

export const MFASettings: FC<Props> = ({ userId, email, isMandatoryModalEnabled, refetchUser }) => {
  const [updateUser, { loading: updateUserLoading }] = useMutation<updateUserByAdmin, updateUserByAdminVariables>(
    UPDATE_USER_BY_ADMIN
  )

  const apolloClient = useApolloClient()
  const { data, loading } = useQuery<findUserMfaInfo, findUserMfaInfoVariables>(FIND_USER_MFA_INFO, {
    variables: {
      userId: userId,
    },
  })

  const [disableMfa, { loading: disableMfaLoading }] = useMutation<disableMfaByAdmin, disableMfaByAdminVariables>(
    DISABLE_MFA_BY_ADMIN
  )
  const [enableMfa, { loading: enableMfaLoading }] = useMutation<enableMfaByAdmin, enableMfaByAdminVariables>(
    ENABLE_MFA_BY_ADMIN
  )

  const handleEnableMfa = async () => {
    try {
      await enableMfa({
        variables: {
          userId: userId,
        },
        refetchQueries: [FIND_USER_MFA_INFO],
      })

      toast.success('2FA enabled successfully')
    } catch (error) {
      console.error(error)
      toast.error('Failed to enable 2FA')
    }
  }

  const handleDisableMfa = async () => {
    try {
      await disableMfa({
        variables: {
          userId: userId,
        },
      })

      apolloClient.writeQuery({
        query: FIND_USER_MFA_INFO,
        variables: {
          userId: userId,
        },
        data: {
          findUserMfaInfo: {
            ...data?.findUserMfaInfo,
            isMfaEnabled: false,
          },
        },
      })
      toast.success('2FA disabled successfully')
    } catch (error) {
      console.error(error)
      toast.error('Failed to disable 2FA')
    }
  }

  const handleUpdateUser = async (isMFAMandatory: boolean) => {
    try {
      await updateUser({
        variables: {
          updateUserInput: {
            id: userId,
            isMFAMandatory,
            email,
          },
        },
      })
      refetchUser()
    } catch (error) {
      console.error(error)
      toast.error('Failed to update MFA settings')
    }
  }

  if (loading) {
    return <PageLoader />
  }

  const mfaData = data?.findUserMfaInfo
  if (!mfaData) {
    return <div>No MFA data found</div>
  }

  const { isMfaEnabled, mfaType, phoneNumber } = mfaData

  const getMfaTypeText = () => {
    if (mfaType === MfaTypeEnum.APP) {
      return 'Authenticator App'
    }

    if (mfaType === MfaTypeEnum.SMS) {
      return 'SMS'
    }

    return '-'
  }

  const handleChangeMfa = () => {
    if (isMfaEnabled) {
      disableMfa()
    } else {
      enableMfa()
    }
  }

  const disabled = disableMfaLoading || enableMfaLoading || updateUserLoading

  return (
    <>
      <div css={classes.heading}>
        <Typography variant="title3" css={commonClasses.title}>
          2FA Authentication
        </Typography>
        <FormControlLabel
          label="Enabled"
          control={<Checkbox checked={isMfaEnabled} onChange={handleChangeMfa} disabled={disabled} />}
          css={classes.checkbox}
        />
      </div>

      <Divider />
      <div css={classes.mfaSettingsWrapper}>
        <div css={classes.mfaInfoWrapper}>
          <div css={classes.mfaInfoItem}>
            <Typography variant="title3" css={classes.title}>
              Phone Number
            </Typography>
            <Typography css={classes.title}>{phoneNumber ? phoneNumber : '-'}</Typography>
          </div>
          <div css={classes.mfaInfoItem}>
            <Typography variant="title3" css={classes.title}>
              Selected 2FA
            </Typography>
            <Typography css={classes.title}>{getMfaTypeText()}</Typography>
          </div>
        </div>

        <div css={classes.buttonsWrapper}>
          {isMfaEnabled ? (
            <Button
              variant="outlined"
              color="blue"
              size="large"
              fullWidth
              onClick={handleDisableMfa}
              disabled={disabled}
              css={classes.dangerButton}
            >
              Disable 2FA on Sign In
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="blue"
              size="large"
              fullWidth
              onClick={handleEnableMfa}
              disabled={disabled}
            >
              Enable 2FA on Sign In
            </Button>
          )}
          <Button
            variant="outlined"
            color="blue"
            size="large"
            fullWidth
            onClick={() => handleUpdateUser(!isMandatoryModalEnabled)}
            css={isMandatoryModalEnabled ? classes.dangerButton : undefined}
            disabled={disabled}
          >
            {isMandatoryModalEnabled ? 'Disable' : 'Enable'} 2FA Mandatory Modal
          </Button>
        </div>
      </div>
    </>
  )
}
